// The photo step: its top bar, the deck and shutter footer, and the photo viewer.
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { doneLabel, photoPrompt } from "../../lib/photoStep";
import { mark } from "../../lib/stepLog";
import { MAX_SHOTS } from "./useCaptureFlow";
import { DEBUG_SCORE, shownFlag } from "./shotCapture";

export function PhotoBar({ cam, discard, flipCamera, flipping, iconBtn, isPreset, photoSub, photoTitle, photoUi, saving, scanUi, toggleTorch, torchOn }) {
  return (
    <>
      {photoUi ? (
        <div className="cap-sbar">
          <button type="button" className={iconBtn} aria-label={isPreset ? "Close" : "Discard this item and scan again"} disabled={saving} onClick={discard}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <div className="cap-stitle">
            <span>{photoTitle}</span>
            <span>{photoSub}</span>
          </div>
          <span className="cap-sright">
            {cam.canFlip && (cam.live || !scanUi) ? (
              <button type="button" className={iconBtn} aria-label="Flip camera" disabled={flipping} onClick={flipCamera}>
                <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><path d="M9 13a3 3 0 0 1 5.2-2M15 13a3 3 0 0 1-5.2 2" /><path d="M14.5 9.5v1.6h-1.6M9.5 16.5v-1.6h1.6" /></svg>
              </button>
            ) : null}
            {cam.torchOk && (cam.live || !scanUi) ? (
              <button
                type="button"
                className={iconBtn + (torchOn ? " cap-sbtn--on" : "")}
                aria-label="Torch"
                aria-pressed={torchOn}
                disabled={flipping}
                onClick={toggleTorch}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" aria-hidden="true"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
              </button>
            ) : null}
          </span>
        </div>
      ) : null}
    </>
  );
}

export function PhotoFooter({ addFiles, canConfirm, confirmCapture, fileShotsRef, isNewItem, offlineBulk, photoTiles, photoUi, removeShot, saveError, savedUi, saving, setInspecting, shots, takeShot, tilesRef }) {
  // The × on a tile: the tile shrinks away, then goes, and the line above says so for a moment.
  const [leavingTile, setLeavingTile] = useState(null);
  const [said, setSaid] = useState(false);
  const saidTimer = useRef(null);
  useEffect(() => () => clearTimeout(saidTimer.current), []);
  const dropTile = (id) => {
    if (leavingTile) return;
    setLeavingTile(id);
    try {
      navigator.vibrate?.(12);
    } catch {
      /* best-effort */
    }
    setTimeout(() => {
      removeShot(id);
      setLeavingTile(null);
      setSaid(true);
      clearTimeout(saidTimer.current);
      saidTimer.current = setTimeout(() => setSaid(false), REMOVED_MS);
    }, OUT_MS);
  };
  const prompt = said ? "Photo removed" : photoPrompt({ isNewItem, shotCount: shots.length, offline: offlineBulk, onFileCount: photoTiles.filter((t) => t.type === "saved").length });
  return (
    <>
      {photoUi ? (
        <>
          <div className={"cap-spill cap-pfade" + (said ? " cap-spill--said" : "")} key={prompt}>
            <span>{prompt}</span>
          </div>
          <div className="cap-pstrip" ref={tilesRef}>
            {photoTiles.map((t) => {
              if (t.type === "divider") return <i className="cap-pdiv" key={t.key} aria-hidden="true" />;
              if (t.type === "front")
                return (
                  <div className="cap-pt cap-pt--dash cap-pt--front" key={t.key}>
                    Front
                  </div>
                );
              if (t.type === "plus")
                return (
                  <button type="button" className="cap-pt cap-pt--dash" key={t.key} aria-label="Take photo" onClick={takeShot} disabled={saving || shots.length >= MAX_SHOTS}>
                    +
                  </button>
                );
              if (t.type === "saved")
                return (
                  <button
                    type="button"
                    className={"cap-pt" + (t.image.kind === "packshot" ? " cap-pt--pack" : "")}
                    key={t.key}
                    onClick={() => setInspecting({ key: t.key })}
                    aria-label={"Open saved photo " + (t.index + 1)}
                  >
                    <img src={t.image.url} alt="" decoding="async" loading="lazy" />
                    {t.tag ? <span className="cap-pt-tag">{t.tag}</span> : null}
                  </button>
                );
              const s = t.shot;
              return (
                <div className={"cap-pt cap-pt--shot" + (s.pending ? " cap-pt--pending" : "") + (t.tag === "New" ? " cap-pt--new" : "") + (leavingTile === s.id ? " cap-pt--out" : "")} key={t.key}>
                  <button type="button" className="cap-pt-open" onClick={() => setInspecting({ key: t.key })} aria-label={"Inspect new photo " + (t.index + 1)}>
                    <img src={s.previewUrl || s.url} alt={"Photo " + (t.index + 1)} />
                  </button>
                  {s.pending ? <span className="cap-pt-wait" /> : null}
                  {offlineBulk ? <span className="cap-pt-off" aria-label="Waiting for signal" /> : null}
                  {t.tag ? <span className={"cap-pt-tag" + (t.tag === "New" ? " cap-pt-tag--new" : "")}>{t.tag}</span> : null}
                  <button type="button" className="cap-pt-x" onClick={() => dropTile(s.id)} aria-label={"Remove new photo " + (t.index + 1)}>
                    <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M1.5 1.5l9 9M10.5 1.5l-9 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
                  </button>
                </div>
              );
            })}
          </div>
          {saveError ? (
            <p className="cap-error" role="alert">
              {saveError}
            </p>
          ) : null}
          <input ref={fileShotsRef} type="file" accept="image/*" multiple className="cap-file" tabIndex={-1} aria-hidden="true" onChange={addFiles} />
          <div className="cap-prow">
            <button type="button" className="cap-pup" onClick={() => fileShotsRef.current?.click()} disabled={saving || shots.length >= MAX_SHOTS}>
              <span>
                <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" aria-hidden="true"><path d="M12 15V3M7 8l5-5 5 5" /><path d="M4 14v4a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-4" /></svg>
              </span>
              Upload
            </button>
            <button type="button" className={"cap-pshut"} onClick={takeShot} disabled={saving || shots.length >= MAX_SHOTS} aria-label="Take photo" />
            <button type="button" className="cap-pdone" onClick={confirmCapture} disabled={!canConfirm}>
              {doneLabel({ quick: savedUi, isNewItem, shotCount: shots.length })}
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

/** The tray's photos in tray order, each shaped for the viewer. */
function viewerItems(photoTiles) {
  const out = [];
  for (const t of photoTiles) {
    if (t.type === "saved") {
      const img = t.image;
      out.push({
        key: t.key, saved: true, id: img.id, image: img, url: img.url, fullUrl: img.fullUrl || null,
        label: img.kind === "packshot" ? "Pack shot" : "Saved",
        tone: img.kind === "packshot" ? "pack" : "saved",
        canRemove: img.kind === "photo" && Boolean(img.id) && img.removable !== false,
      });
    } else if (t.type === "shot") {
      const note = [shownFlag(t.shot.flag), DEBUG_SCORE && t.shot.q ? ["s", "g", "f", "x", "y", "k"].map((k) => k + t.shot.q[k]).join(" ") : null].filter(Boolean).join(" · ");
      out.push({
        key: t.key, saved: false, id: t.shot.id, url: t.shot.previewUrl || t.shot.url, fullUrl: null,
        label: note ? "New · " + note : "New", tone: "new", canRemove: true,
      });
    }
  }
  return out;
}

const GAP = 12; // px between slides
// A swipe settles over a time that follows the distance left (a short flick settles fast, a
// half-way drag takes a little longer), on an ease-out curve, so it keeps the finger's speed.
const SNAP_MIN_MS = 140;
const SNAP_MAX_MS = 300;
const SNAP_EASE = "cubic-bezier(.22,.61,.36,1)";
const FLICK = 0.35; // px/ms over the last ~100 ms: a flick turns the page however short
const DIM = 0.35; // the neighbours' opacity
const OUT_MS = 220; // the removed photo shrinking away
const REMOVED_MS = 900; // "Photo removed" on screen
// Zoom, to check small print: pinch, or double-tap to 2.5x at the tap. While zoomed one finger pans
// and swiping is off; back at 1x it swipes again.
const MAX_ZOOM = 5;
const TAP_ZOOM = 2.5;
const NO_ZOOM = { s: 1, x: 0, y: 0 };

/** One slide: the thumbnail at once (blurred), the original once it has loaded. */
function ViewerSlide({ item, current, offset, onPick, zoom }) {
  const [full, setFull] = useState(false);
  // The photo's own shape, so the rounded card is the picture and not a letterbox around it.
  const [ratio, setRatio] = useState(3 / 4);
  useEffect(() => {
    setFull(false);
    if (!item.fullUrl) return undefined;
    let live = true;
    // Decoded off the main thread BEFORE the swap: a 3,000 px photo decoding on the swap
    // dropped frames from the swipe that brought it on screen.
    const im = new Image();
    im.decoding = "async";
    im.src = item.fullUrl;
    (im.decode ? im.decode() : new Promise((r, j) => { im.onload = r; im.onerror = j; }))
      .then(() => live && setFull(true))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [item.fullUrl]);
  return (
    <div
      className={"cap-v-slide" + (current ? " cap-v-slide--cur" : offset < 0 ? " cap-v-slide--prev" : " cap-v-slide--next")}
      style={{ left: `calc(${offset} * (100% + ${GAP}px))` }}
      aria-hidden={current ? undefined : "true"}
      onClick={current ? undefined : onPick}
    >
      <img
        decoding="async"
        src={full ? item.fullUrl : item.url}
        alt=""
        draggable="false"
        className={item.fullUrl && !full ? "cap-v-soft" : ""}
        onLoad={(e) => {
          const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
          if (w && h) setRatio(w / h);
        }}
        style={{
          aspectRatio: String(ratio),
          width: `min(100cqw, ${offset ? 86 : 100}cqh * ${ratio})`,
          ...(current && zoom && zoom.s > 1 ? { transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})`, borderRadius: 0 } : null),
        }}
      />
    </div>
  );
}

function PhotoViewer({ inspecting, photoTiles, removeSaved, removeShot, setInspecting }) {
  const items = viewerItems(photoTiles);
  const [curKey, setCurKey] = useState(inspecting.key);
  // Removing: the photo shrinks away (out), then "Photo removed" stays a moment (said).
  const [out, setOut] = useState(false);
  const [said, setSaid] = useState(false);
  const saidTimer = useRef(null);
  useEffect(() => () => clearTimeout(saidTimer.current), []);
  const [asking, setAsking] = useState(false);
  const found = items.findIndex((it) => it.key === curKey);
  const lastIdx = useRef(0);
  const idx = found >= 0 ? found : Math.max(0, Math.min(lastIdx.current, items.length - 1));
  lastIdx.current = idx;
  const item = items[idx];
  const seen = useRef(new Set());
  const trackRef = useRef(null);
  const stripRef = useRef(null);
  const drag = useRef(null);
  const [zoom, setZoom] = useState(NO_ZOOM);
  const zoomRef = useRef(NO_ZOOM);
  zoomRef.current = zoom;
  const pts = useRef(new Map()); // active pointers: id -> {x, y}
  const pinch = useRef(null);
  const lastTap = useRef(null);
  const close = () => setInspecting(null);

  useEffect(() => {
    if (!items.length) setInspecting(null);
  }, [items.length]);
  useEffect(() => {
    if (item) seen.current.add(item.key);
    setZoom(NO_ZOOM);
  }, [item?.key]);
  useEffect(() => {
    mark("viewer_open", { kind: item?.saved ? (item.image.kind === "packshot" ? "packshot" : "saved") : "shot", index: idx });
    return () => mark("viewer_close", { seen: seen.current.size });
  }, []);

  // Keep the current thumbnail in view.
  useEffect(() => {
    const box = stripRef.current;
    const el = box?.querySelector('[aria-current="true"]');
    if (el) box.scrollTo({ left: el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2, behavior: "smooth" });
  }, [item?.key]);

  const go = (to) => {
    if (to < 0 || to >= items.length || to === idx) return;
    setAsking(false);
    setZoom(NO_ZOOM);
    setCurKey(items[to].key);
  };
  useEffect(() => {
    const onKey = (e) => {
      if (asking) return;
      if (e.key === "ArrowLeft") go(idx - 1);
      else if (e.key === "ArrowRight") go(idx + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // THE SWIPE MOVES THE TRACK ITSELF, not through React: a state update per finger move
  // re-rendered the whole viewer and lagged. One write per frame; React only hears the result.
  const raf = useRef(0);
  const setX = (x, ms = 0) => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = ms ? `transform ${ms}ms ${SNAP_EASE}` : "none";
    el.style.transform = x ? `translate3d(${x}px,0,0)` : "";
  };
  const snapping = useRef(false);
  const width = () => trackRef.current?.getBoundingClientRect().width || 300;
  const slideEl = (cls) => [...(trackRef.current?.children || [])].find((el) => el.classList.contains(cls));
  // The neighbour being pulled in brightens with the drag, and the current one dims: the
  // opacity follows the finger instead of jumping when the swipe ends.
  const shade = (x, ms = 0) => {
    const p = Math.min(1, Math.abs(x) / (width() + GAP));
    const cur = slideEl("cap-v-slide--cur");
    const prev = slideEl("cap-v-slide--prev");
    const next = slideEl("cap-v-slide--next");
    const t = ms ? `opacity ${ms}ms ${SNAP_EASE}` : "none";
    for (const [el, o] of [[cur, 1 - (1 - DIM) * p], [x < 0 ? next : prev, DIM + (1 - DIM) * p], [x < 0 ? prev : next, DIM]]) {
      if (!el) continue;
      el.style.transition = t;
      el.style.opacity = String(o);
    }
  };
  const lastX = useRef(0);
  // dir: 1 = next, -1 = previous, 0 = snap back. v: the release speed (px/ms).
  const settle = (dir, v = 0) => {
    cancelAnimationFrame(raf.current);
    const target = dir ? -dir * (width() + GAP) : 0;
    const left = Math.abs(target - lastX.current);
    const ms = Math.round(Math.max(SNAP_MIN_MS, Math.min(SNAP_MAX_MS, Math.abs(v) > FLICK ? left / Math.abs(v) : (left / width()) * SNAP_MAX_MS * 1.2)));
    lastX.current = target;
    setX(target, ms);
    shade(target, ms);
    if (!dir) return;
    snapping.current = true;
    const to = idx + dir;
    setTimeout(() => {
      // The new photo and the track back at 0 land in the same frame: no jump.
      flushSync(() => go(to));
      setX(0);
      for (const el of trackRef.current?.children || []) {
        el.style.transition = "";
        el.style.opacity = "";
      }
      lastX.current = 0;
      snapping.current = false;
    }, ms);
  };
  // The stage box, for the zoom maths (points relative to its centre) and the pan limits.
  const box = () => trackRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 300, height: 400 };
  const clampZoom = (z) => {
    const s = Math.min(MAX_ZOOM, Math.max(1, z.s));
    if (s <= 1.01) return NO_ZOOM;
    const b = box();
    const mx = ((s - 1) * b.width) / 2;
    const my = ((s - 1) * b.height) / 2;
    return { s, x: Math.max(-mx, Math.min(mx, z.x)), y: Math.max(-my, Math.min(my, z.y)) };
  };
  /** Zoom to `s` keeping the point under (cx, cy) where it is. */
  const zoomAt = (z, s, cx, cy) => {
    const b = box();
    const px = cx - (b.left + b.width / 2);
    const py = cy - (b.top + b.height / 2);
    const k = s / z.s;
    return clampZoom({ s, x: px - (px - z.x) * k, y: py - (py - z.y) * k });
  };
  const onDown = (e) => {
    if (asking || out || snapping.current || e.button > 0) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.current.size === 2) {
      for (const id of pts.current.keys()) e.currentTarget.setPointerCapture?.(id);
      // A second finger: pinch, and whatever swipe had started is dropped.
      drag.current = null;
      const [a, b] = [...pts.current.values()];
      pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, z: zoomRef.current, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
      return;
    }
    if (pts.current.size > 2) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: Date.now(), live: false, pan: zoomRef.current.s > 1 ? { ...zoomRef.current } : null };
  };
  const onMove = (e) => {
    if (pts.current.has(e.pointerId)) pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pts.current.size >= 2) {
      const [a, b] = [...pts.current.values()];
      const p = pinch.current;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const s = p.z.s * (Math.hypot(a.x - b.x, a.y - b.y) / p.d);
      const z = zoomAt(p.z, Math.min(MAX_ZOOM, Math.max(1, s)), p.mid.x, p.mid.y);
      setZoom(clampZoom({ ...z, x: z.x + mid.x - p.mid.x, y: z.y + mid.y - p.mid.y }));
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (d.pan) {
      // Zoomed: one finger moves the photo, it does not swipe.
      setZoom(clampZoom({ s: d.pan.s, x: d.pan.x + e.clientX - d.x, y: d.pan.y + e.clientY - d.y }));
      if (!d.moved && Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 8) {
        d.moved = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
      return;
    }
    const mx = e.clientX - d.x;
    const my = e.clientY - d.y;
    if (!d.live) {
      if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
      if (Math.abs(my) > Math.abs(mx)) {
        d.moved = true; // mostly vertical: not a swipe, not a tap
        return;
      }
      d.live = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    const edge = (mx > 0 && idx === 0) || (mx < 0 && idx === items.length - 1);
    const x = edge ? mx * 0.3 : mx;
    const now = performance.now();
    d.trail = [...(d.trail || []).filter((p) => now - p.t < 100), { t: now, x: e.clientX }];
    lastX.current = x;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      setX(x);
      shade(x);
    });
  };
  const onUp = (e) => {
    pts.current.delete(e.pointerId);
    if (pinch.current) {
      if (pts.current.size < 2) pinch.current = null;
      return; // the finger left after a pinch starts nothing
    }
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.live) {
      const mx = e.clientX - d.x;
      const tr = d.trail || [];
      const v = tr.length > 1 ? (tr.at(-1).x - tr[0].x) / Math.max(1, tr.at(-1).t - tr[0].t) : 0;
      // Past a third of the way, or flicked that way (a flick back the other way cancels).
      const flick = Math.abs(v) > FLICK && Math.sign(v) === Math.sign(mx);
      const far = Math.abs(mx) > width() * 0.33 ? Math.sign(v) !== -Math.sign(mx) || Math.abs(v) < FLICK : flick;
      const dir = mx < 0 ? 1 : -1;
      settle(far && idx + dir >= 0 && idx + dir < items.length ? dir : 0, v);
      return;
    }
    if (!d.moved) {
      // A tap. Two within 300 ms near each other: zoom in at the tap, or back out.
      const now = Date.now();
      const t = lastTap.current;
      if (t && now - t.t < 300 && Math.hypot(t.x - e.clientX, t.y - e.clientY) < 30) {
        lastTap.current = null;
        setZoom((z) => (z.s > 1 ? NO_ZOOM : zoomAt(z, TAP_ZOOM, e.clientX, e.clientY)));
      } else lastTap.current = { t: now, x: e.clientX, y: e.clientY };
      return;
    }
  };
  const onCancel = (e) => {
    pts.current.delete(e.pointerId);
    if (pts.current.size < 2) pinch.current = null;
    if (drag.current?.live) settle(0);
    drag.current = null;
  };

  const confirmRemove = () => {
    if (out) return;
    const gone = item;
    const next = items[idx + 1] || items[idx - 1] || null;
    setAsking(false);
    setOut(true);
    // A new photo is gone at once; a saved one only once the server says so (a refusal
    // must not flash "Photo removed" before the tile comes back with an error).
    const say = () => {
      setSaid(true);
      clearTimeout(saidTimer.current);
      saidTimer.current = setTimeout(() => setSaid(false), REMOVED_MS);
    };
    if (!gone.saved) say();
    try {
      navigator.vibrate?.(12);
    } catch {
      /* best-effort */
    }
    setTimeout(() => {
      if (gone.saved) Promise.resolve(removeSaved(gone.image)).then((ok) => ok && say());
      else {
        mark("shot_remove", { via: "viewer" });
        removeShot(gone.id);
      }
      setOut(false);
      if (next) {
        setZoom(NO_ZOOM);
        setCurKey(next.key);
      } else close();
    }, OUT_MS);
  };

  if (!item) return null;
  const divAt = items.findIndex((it) => !it.saved);
  return (
    <div className="cap-inspect" role="dialog" aria-modal="true" aria-label="Photo viewer">
      <div className="cap-v-bar">
        <button type="button" className="cap-v-x" onClick={close} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <div className="cap-v-count">
          <span><b>{idx + 1}</b> / {items.length}</span>
          <span className={"cap-v-tag cap-v-tag--" + item.tone}>{item.label}</span>
        </div>
        <span className="cap-v-x cap-v-x--ghost" aria-hidden="true" />
      </div>
      <div className="cap-v-stage" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onCancel}>
        <div className={"cap-v-track" + (out ? " cap-v-track--out" : "")} ref={trackRef}>
          {[idx - 1, idx, idx + 1]
            .filter((n) => n >= 0 && n < items.length)
            .map((n) => (
              <ViewerSlide key={items[n].key} item={items[n]} current={n === idx} offset={n - idx} onPick={() => go(n)} zoom={zoom} />
            ))}
        </div>
        {said ? (
          <div className="cap-v-said" role="status">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            Photo removed
          </div>
        ) : null}
      </div>
      <div className="cap-v-dots" aria-hidden="true">
        {items.map((it, n) => (
          <i key={it.key} className={n === idx ? "on" : ""} />
        ))}
      </div>
      <div className="cap-v-strip" ref={stripRef}>
        {items.map((it, n) => (
          <span key={it.key} className="cap-v-cell">
            {n === divAt && n > 0 ? <i className="cap-v-div" aria-hidden="true" /> : null}
            <button type="button" className={"cap-v-th" + (n === idx ? " on" : "")} aria-current={n === idx ? "true" : undefined} aria-label={"Photo " + (n + 1)} onClick={() => go(n)}>
              <img src={it.url} alt="" draggable="false" />
            </button>
          </span>
        ))}
      </div>
      <div className="cap-v-act">
        {item.canRemove ? (
          <button type="button" className="cap-v-rm" disabled={out} onClick={() => !out && setAsking(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>
            Remove
          </button>
        ) : null}
      </div>
      {asking ? (
        <div className="cap-v-dim" onClick={() => setAsking(false)}>
          <div className="cap-v-sheet" role="alertdialog" aria-labelledby="cap-v-st" onClick={(e) => e.stopPropagation()}>
            <img src={item.url} alt="" />
            <div className="cap-v-sheet-t">
              <strong id="cap-v-st">Remove this photo?</strong>
              <span>{item.saved ? "It will be deleted from the product." : "It is not saved."}</span>
            </div>
            <div className="cap-v-sheet-b">
              <button type="button" className="cap-v-no" onClick={() => setAsking(false)}>
                Cancel
              </button>
              <button type="button" className="cap-v-yes" onClick={confirmRemove}>
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PhotoInspect({ inspecting, photoTiles, removeSaved, removeShot, setInspecting }) {
  return inspecting ? <PhotoViewer inspecting={inspecting} photoTiles={photoTiles} removeSaved={removeSaved} removeShot={removeShot} setInspecting={setInspecting} /> : null;
}
